import { useEffect, useState } from "react";
import { osApi, formatINR } from "./osApi";
import { toast } from "sonner";

export default function OSAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { osApi.analytics().then(setData).catch(() => toast.error("Failed")); }, []);

  if (!data) return <div className="os-card p-8 text-center" style={{color:"var(--os-text-muted)"}}>Loading analytics…</div>;
  const total = Object.values(data.source_counts).reduce((s, n) => s + n, 0) || 1;
  const funnelTotal = Object.values(data.funnel).reduce((s, n) => s + n, 0) || 1;

  return (
    <div className="space-y-6" data-testid="os-analytics-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-sm mt-1" style={{color:"var(--os-text-muted)"}}>Sources, funnel, revenue, agent leaderboard.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Commission (open pipeline)" value={formatINR(data.revenue.commission)} />
        <MetricCard label="Deal Value (open)" value={formatINR(data.revenue.deal_value)} />
        <MetricCard label="Loan Volume" value={formatINR(data.revenue.loan_amount)} />
        <MetricCard label="Loan Conversion" value={`${data.loan_conversion_pct}%`} sub={`${data.loans_disbursed}/${data.loans_pipeline} disbursed`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="os-card p-5">
          <div className="text-xs uppercase tracking-widest mb-4" style={{color:"var(--os-text-muted)"}}>Leads by Source</div>
          {Object.entries(data.source_counts).map(([k, n]) => (
            <div key={k} className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span>{k}</span><span className="os-mono">{n}</span>
              </div>
              <div className="h-1.5 rounded-full" style={{background:"var(--os-card-2)"}}>
                <div className="h-full rounded-full" style={{width: `${(n/total)*100}%`, background:"var(--os-accent)"}} />
              </div>
            </div>
          ))}
        </div>

        <div className="os-card p-5">
          <div className="text-xs uppercase tracking-widest mb-4" style={{color:"var(--os-text-muted)"}}>Conversion Funnel</div>
          {Object.entries(data.funnel).map(([k, n]) => (
            <div key={k} className="mb-2.5">
              <div className="flex justify-between text-xs mb-1">
                <span>{k}</span><span className="os-mono">{n}</span>
              </div>
              <div className="h-1.5 rounded-full" style={{background:"var(--os-card-2)"}}>
                <div className="h-full rounded-full" style={{width: `${(n/funnelTotal)*100}%`, background: k === "Closed" ? "var(--os-green)" : k === "Lost" ? "var(--os-red)" : "var(--os-accent)"}} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="os-card p-5">
        <div className="text-xs uppercase tracking-widest mb-4" style={{color:"var(--os-text-muted)"}}>Agent Leaderboard</div>
        <div className="overflow-x-auto">
          <table className="os-table">
            <thead><tr><th>Rank</th><th>Agent</th><th>Leads</th><th>Closed</th><th>Revenue</th></tr></thead>
            <tbody>
              {data.leaderboard.map((a, i) => (
                <tr key={a.id} data-testid={`os-leader-${i}`}>
                  <td className="os-mono">#{i + 1}</td>
                  <td className="font-medium">{a.name}</td>
                  <td className="os-mono">{a.leads}</td>
                  <td className="os-mono" style={{color: a.closed ? "var(--os-green)" : "inherit"}}>{a.closed}</td>
                  <td className="os-mono">{formatINR(a.revenue)}</td>
                </tr>
              ))}
              {data.leaderboard.length === 0 && <tr><td colSpan={5} className="text-center py-8" style={{color:"var(--os-text-muted)"}}>No revenue yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub }) {
  return (
    <div className="os-metric">
      <div className="os-metric-label">{label}</div>
      <div className="os-metric-value os-mono">{value}</div>
      {sub && <div className="os-metric-sub">{sub}</div>}
    </div>
  );
}
